<template>
  <div class="runner">
    <div class="runner-stage">
      <canvas
        ref="canvasEl"
        class="runner-canvas"
        tabindex="0"
        :aria-label="t('duel.queueRun')"
        @pointerdown="onPointer"
      />
      <p v-if="dead" class="runner-banner">{{ t('duel.queueRunOver') }}</p>
      <p v-else-if="!jumped" class="runner-banner runner-hint">{{ t('duel.queueRun') }}</p>
      <output v-if="coinFlash" class="coin-award">
        <CoinToken size="md" shine bob />
        <span>{{ t('duel.queueStarCoin') }}</span>
      </output>
    </div>
    <p class="runner-score">
      {{ t('duel.queueRunCount', { n: score }) }}
      <template v-if="best > 0"> · {{ t('duel.queueStarBest', { n: best }) }}</template>
    </p>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import type { RunnerMissionRow } from '@shared/index'
import { api } from '../api/http'
import { useI18n } from '../i18n/t'
import { useAuthStore } from '../stores/auth'
import { useMissionsStore } from '../stores/missions'
import { useWalletStore } from '../stores/wallet'
import { CARD_BACK_URL } from '../utils/cardImages'
import CoinToken from './CoinToken.vue'

const BEST_KEY = 'dc-queue-run-best'
const BASE_SPEED = 170
const MAX_SPEED = 340
const GRAVITY = 1850
const JUMP_V = 480
const RUNNER_X = 28
const CARD_W_OVER_H = 421 / 614
const GIRL_URL = '/assets/queue/dark-magician-girl.png'
const GIRL_FRAMES = 4
const GIRL_CELL_W = 21
const GIRL_CELL_H = 34

const { t } = useI18n()
const auth = useAuthStore()
const wallet = useWalletStore()
const missions = useMissionsStore()
const canvasEl = ref<HTMLCanvasElement | null>(null)
const score = ref(0)
const best = ref(readBest())
const dead = ref(false)
const jumped = ref(false)
const coinFlash = ref(false)

type Obstacle = { x: number; w: number; h: number; passed: boolean }

let raf = 0
let alive = true
let last = 0
let width = 0
let height = 0
let groundY = 118
let dpr = 1
let distance = 0
let speed = BASE_SPEED
let lift = 0
let vy = 0
let grounded = true
let coyote = 0
let runPhase = 0
let spawnIn = 0.85
let obstacles: Obstacle[] = []
let rolling = false
let claimTail: Promise<void> = Promise.resolve()
let coinTimer: ReturnType<typeof setTimeout> | null = null
let observer: ResizeObserver | null = null
let runnerImg: HTMLImageElement | null = null
let backImg: HTMLImageElement | null = null
let runnerW = GIRL_CELL_W * 2
let runnerH = GIRL_CELL_H * 2

function readBest(): number {
  try {
    const n = Number(localStorage.getItem(BEST_KEY))
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
  } catch {
    return 0
  }
}

function remember(n: number): void {
  if (n <= best.value) return
  best.value = n
  try {
    localStorage.setItem(BEST_KEY, String(n))
  } catch {
    /* private mode */
  }
}

function reset(): void {
  distance = 0
  speed = BASE_SPEED
  lift = 0
  vy = 0
  grounded = true
  coyote = 0
  runPhase = 0
  spawnIn = 0.85
  obstacles = []
  score.value = 0
  dead.value = false
  jumped.value = false
  last = 0
}

function jump(): void {
  if (dead.value) {
    reset()
    return
  }
  if (!grounded && coyote <= 0) return
  vy = -JUMP_V
  grounded = false
  coyote = 0
  jumped.value = true
}

function onPointer(event: PointerEvent): void {
  event.preventDefault()
  jump()
}

function onKey(event: KeyboardEvent): void {
  if (event.code !== 'Space' && event.code !== 'ArrowUp') return
  if (event.repeat) return
  const target = event.target
  if (target instanceof HTMLElement && target.closest('button, a, input, textarea')) return
  event.preventDefault()
  jump()
}

function fit(): void {
  const canvas = canvasEl.value
  if (!canvas) return
  width = canvas.clientWidth
  height = canvas.clientHeight
  groundY = height - 16
  dpr = Math.min(2, window.devicePixelRatio || 1)
  canvas.width = Math.max(1, Math.round(width * dpr))
  canvas.height = Math.max(1, Math.round(height * dpr))
}

function hits(obstacle: Obstacle): boolean {
  const rx = RUNNER_X + 4
  const rw = runnerW - 8
  const ry = groundY - lift - runnerH + 6
  const rh = runnerH - 10
  const tall = obstacle.h >= 40
  const side = tall ? 5 : 2
  const top = tall ? 12 : 3
  const ox = obstacle.x + side
  const ow = obstacle.w - side * 2
  const oy = groundY - obstacle.h + top
  const oh = obstacle.h - top - 3
  return rx < ox + ow && rx + rw > ox && ry < oy + oh && ry + rh > oy
}

function step(dt: number): void {
  speed = Math.min(MAX_SPEED, BASE_SPEED + distance * 0.018)
  distance += speed * dt
  const next = Math.floor(distance / 14)
  if (next !== score.value) {
    score.value = next
    remember(next)
  }

  if (grounded) {
    coyote = 0.08
    runPhase += dt * speed * 0.045
  } else {
    coyote = Math.max(0, coyote - dt)
    vy += GRAVITY * dt
    lift -= vy * dt
    if (lift <= 0) {
      lift = 0
      vy = 0
      grounded = true
    }
  }

  for (const obstacle of obstacles) obstacle.x -= speed * dt

  if (obstacles.some(hits)) {
    dead.value = true
    remember(score.value)
  } else {
    for (const obstacle of obstacles) {
      if (!obstacle.passed && obstacle.x + obstacle.w < RUNNER_X) {
        obstacle.passed = true
        claimTail = claimTail.then(() => claimCoin())
      }
    }
  }

  obstacles = obstacles.filter((obstacle) => obstacle.x + obstacle.w > -8)

  spawnIn -= dt
  if (spawnIn <= 0) {
    const tall = Math.random() < 0.4
    const h = tall ? 44 : 30
    const ratio = backImg && backImg.naturalWidth ? backImg.naturalWidth / backImg.naturalHeight : CARD_W_OVER_H
    obstacles.push({
      x: width + 4,
      w: Math.round(h * ratio),
      h,
      passed: false,
    })
    spawnIn = Math.max(0.95, 1.45 - distance / 4500) + Math.random() * 0.35
  }
}

async function claimCoin(): Promise<void> {
  const authToken = auth.token
  if (!authToken || rolling) return
  rolling = true
  try {
    const steps = score.value
    const payload = await api<{
      coin: boolean
      coins: number
      runner?: RunnerMissionRow[]
    }>('/economy/queue-star', {
      method: 'POST',
      token: authToken,
      body: JSON.stringify({ score: steps }),
    })
    if (payload.runner) missions.applyRunner(payload.runner)
    if (typeof payload.coins === 'number') wallet.setCoins(payload.coins)
    if (!payload.coin) return
    coinFlash.value = true
    if (coinTimer) clearTimeout(coinTimer)
    coinTimer = window.setTimeout(() => {
      coinFlash.value = false
    }, 1400)
  } catch {
    /* a miss or a closed queue stays on the run */
  } finally {
    rolling = false
  }
}
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, r)
}

function drawCard(ctx: CanvasRenderingContext2D, obstacle: Obstacle): void {
  const x = obstacle.x
  const y = groundY - obstacle.h
  if (backImg) {
    ctx.drawImage(backImg, x, y, obstacle.w, obstacle.h)
    return
  }
  ctx.fillStyle = '#7f1d1d'
  roundRect(ctx, x, y, obstacle.w, obstacle.h, 2)
  ctx.fill()
}

function drawRunner(ctx: CanvasRenderingContext2D): void {
  if (!runnerImg) return
  const frame = grounded ? Math.floor(runPhase) % GIRL_FRAMES : 1
  ctx.save()
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(
    runnerImg,
    frame * GIRL_CELL_W,
    0,
    GIRL_CELL_W,
    GIRL_CELL_H,
    RUNNER_X,
    groundY - lift - runnerH,
    runnerW,
    runnerH,
  )
  ctx.restore()
}

function loadSprite(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(src))
    image.src = src
  })
}

function draw(): void {
  const canvas = canvasEl.value
  const ctx = canvas?.getContext('2d')
  if (!ctx || width < 10) return
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = '#0c0e18'
  ctx.fillRect(0, 0, width, height)
  ctx.fillStyle = '#111827'
  ctx.fillRect(0, groundY - 36, width, height - (groundY - 36))
  ctx.strokeStyle = '#e8c547'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(0, groundY + 1)
  ctx.lineTo(width, groundY + 1)
  ctx.stroke()
  const period = 24
  const shift = distance % period
  ctx.fillStyle = 'rgba(253, 230, 138, 0.35)'
  for (let x = -shift; x < width; x += period) {
    ctx.fillRect(x, groundY + 7, 12, 2)
  }
  for (const obstacle of obstacles) drawCard(ctx, obstacle)
  drawRunner(ctx)
}

function frame(now: number): void {
  if (!alive) return
  raf = requestAnimationFrame(frame)
  if (!last) last = now
  const dt = Math.min(0.032, (now - last) / 1000)
  last = now
  if (width >= 10 && !dead.value) step(dt)
  draw()
}

onMounted(() => {
  fit()
  reset()
  void loadSprite(GIRL_URL).then((image) => {
    runnerImg = image
  }).catch(() => {
    runnerImg = null
  })
  void loadSprite(CARD_BACK_URL).then((image) => {
    backImg = image
  }).catch(() => {
    backImg = null
  })
  const canvas = canvasEl.value
  if (canvas) {
    observer = new ResizeObserver(() => fit())
    observer.observe(canvas)
  }
  window.addEventListener('keydown', onKey)
  raf = requestAnimationFrame(frame)
})

onUnmounted(() => {
  alive = false
  cancelAnimationFrame(raf)
  observer?.disconnect()
  window.removeEventListener('keydown', onKey)
  if (coinTimer) clearTimeout(coinTimer)
  remember(score.value)
})
</script>

<style scoped>
.runner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.runner-stage {
  position: relative;
  width: 100%;
  max-width: 440px;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(232, 197, 71, 0.35);
}

.runner-canvas {
  display: block;
  width: 100%;
  height: 150px;
  touch-action: none;
  cursor: pointer;
}

.runner-banner {
  position: absolute;
  left: 0;
  right: 0;
  top: 8px;
  margin: 0;
  text-align: center;
  pointer-events: none;
  color: #fde68a;
  font-size: 14px;
  font-weight: 700;
}

.runner-hint {
  font-weight: 600;
  color: #cbd5e1;
}

.coin-award {
  position: absolute;
  left: 50%;
  top: 36%;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: #fde68a;
  font-size: 14px;
  font-weight: 700;
  pointer-events: none;
  animation: coin-award 1.4s var(--md-motion-emphasized, cubic-bezier(0.05, 0.7, 0.1, 1)) both;
}

@keyframes coin-award {
  0% {
    opacity: 0;
    transform: translateX(-50%) scale(0.55);
  }
  20% {
    opacity: 1;
    transform: translateX(-50%) scale(1.08);
  }
  70% {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(-12px) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .coin-award {
    animation: none;
    opacity: 1;
    transform: translateX(-50%);
  }
}

.runner-score {
  margin: 0;
  max-width: 320px;
  text-align: center;
  font-size: 14px;
  line-height: 1.35;
  color: #cbd5e1;
}
</style>
